import { useCallback, useEffect, useState } from 'react';
import {
  deleteUser,
  fetchUserList,
  registerUser,
  updateUserRole,
} from '../api/userApi';
import { USER_ROLES } from '../constants/userRoles';
import { useAuth } from '../context/AuthContext';

const initialForm = {
  username: '',
  password: '',
  passwordConfirm: '',
  name: '',
  role: 'USER',
};

const getKoreanObjectParticle = (word) => {
  if (!word) return '을';
  const lastChar = word[word.length - 1];
  const code = lastChar.charCodeAt(0);
  if (code < 0xac00 || code > 0xd7a3) return '을';
  return (code - 0xac00) % 28 === 0 ? '를' : '을';
};

function UserManagementPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [roleDrafts, setRoleDrafts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleteModalError, setDeleteModalError] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchUserList();
      const list = Array.isArray(result) ? result : [];
      setUsers(list);
      const drafts = {};
      list.forEach((item) => {
        drafts[item.id] = item.role;
      });
      setRoleDrafts(drafts);
    } catch (loadError) {
      setError(loadError.message || '회원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (form.password !== form.passwordConfirm) {
      setError('비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await registerUser({
        username: form.username.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
      });
      setForm(initialForm);
      setSuccessMessage('회원 등록이 완료되었습니다.');
      await loadUsers();
    } catch (submitError) {
      setError(submitError.message || '회원 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleDraftChange = (userId, role) => {
    setRoleDrafts((prev) => ({ ...prev, [userId]: role }));
    setError('');
    setSuccessMessage('');
  };

  const handleRoleSave = async (targetUser) => {
    const nextRole = roleDrafts[targetUser.id];
    if (!nextRole || nextRole === targetUser.role) {
      return;
    }

    setUpdatingRoleId(targetUser.id);
    setError('');
    setSuccessMessage('');

    try {
      await updateUserRole(targetUser.id, nextRole);
      setSuccessMessage('권한이 변경되었습니다.');
      await loadUsers();
    } catch (roleError) {
      setError(roleError.message || '권한 변경에 실패했습니다.');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleDeleteClick = (targetUser) => {
    if (targetUser.id === user?.id) {
      window.alert('본인 계정은 삭제할 수 없습니다.');
      return;
    }

    setDeleteTarget(targetUser);
    setDeleteConfirmInput('');
    setDeleteModalError('');
    setError('');
    setSuccessMessage('');
  };

  const handleCloseDeleteModal = () => {
    if (deletingId) return;
    setDeleteTarget(null);
    setDeleteConfirmInput('');
    setDeleteModalError('');
  };

  const handleConfirmDelete = async (event) => {
    event.preventDefault();

    if (!deleteTarget) return;

    const trimmedInput = deleteConfirmInput.trim();
    if (trimmedInput !== deleteTarget.username) {
      setDeleteModalError('삭제 대상 아이디가 일치하지 않습니다.');
      return;
    }

    setDeletingId(deleteTarget.id);
    setDeleteModalError('');
    setError('');
    setSuccessMessage('');

    try {
      await deleteUser(deleteTarget.id, trimmedInput);
      setDeleteTarget(null);
      setDeleteConfirmInput('');
      setSuccessMessage('회원이 삭제되었습니다.');
      await loadUsers();
    } catch (deleteError) {
      setDeleteModalError(deleteError.message || '회원 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="page">
      <div className="page-header">
        <h2>회원 관리</h2>
      </div>

      {error ? <p className="login-error">{error}</p> : null}
      {successMessage ? <p className="form-success">{successMessage}</p> : null}

      <div className="user-management-layout">
        <section className="form-section user-management-list">
          <h3>회원 목록</h3>
          <div className="table-wrapper fixed-list-wrapper">
            <table>
              <thead>
                <tr>
                  <th>아이디</th>
                  <th>이름</th>
                  <th>권한</th>
                  <th>권한 변경</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5}>불러오는 중...</td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={5}>등록된 회원이 없습니다.</td>
                  </tr>
                ) : (
                  users.map((item) => {
                    const draftRole = roleDrafts[item.id] ?? item.role;
                    const roleChanged = draftRole !== item.role;

                    return (
                      <tr key={item.id}>
                        <td>{item.username}</td>
                        <td>{item.name}</td>
                        <td>
                          <select
                            value={draftRole}
                            onChange={(event) => handleRoleDraftChange(item.id, event.target.value)}
                          >
                            {USER_ROLES.map((roleOption) => (
                              <option key={roleOption.value} value={roleOption.value}>
                                {roleOption.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="secondary"
                            disabled={!roleChanged || updatingRoleId === item.id}
                            onClick={() => handleRoleSave(item)}
                          >
                            {updatingRoleId === item.id ? '저장 중...' : '저장'}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="danger"
                            disabled={item.id === user?.id || deletingId === item.id}
                            onClick={() => handleDeleteClick(item)}
                          >
                            {deletingId === item.id ? '삭제 중...' : '삭제'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="form-section user-management-register">
          <h3>회원 등록</h3>
          <form className="register-form" onSubmit={handleSubmit}>
            <div className="form-grid profile-form-grid">
              <label className="field" htmlFor="username">
                <span className="field-label">아이디</span>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="off"
                  value={form.username}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="field" htmlFor="name">
                <span className="field-label">이름</span>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="off"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="field" htmlFor="role">
                <span className="field-label">권한</span>
                <select
                  id="role"
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  required
                >
                  {USER_ROLES.map((roleOption) => (
                    <option key={roleOption.value} value={roleOption.value}>
                      {roleOption.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field" htmlFor="password">
                <span className="field-label">비밀번호</span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="field" htmlFor="passwordConfirm">
                <span className="field-label">비밀번호 확인</span>
                <input
                  id="passwordConfirm"
                  name="passwordConfirm"
                  type="password"
                  autoComplete="new-password"
                  value={form.passwordConfirm}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <div className="button-row">
              <button type="submit" disabled={submitting}>
                {submitting ? '등록 중...' : '회원 등록'}
              </button>
            </div>
          </form>
        </section>
      </div>

      {deleteTarget ? (
        <div className="modal-backdrop" onClick={handleCloseDeleteModal}>
          <div
            className="upload-modal confirm-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-user-modal-title"
          >
            <h3 id="delete-user-modal-title">회원 삭제</h3>
            <p className="confirm-modal-description">
              {deleteTarget.name}({deleteTarget.username}) 회원을 삭제합니다.
              <br />
              삭제 후에는 되돌릴 수 없습니다.
            </p>
            <p className="confirm-modal-prompt">
              삭제하시려면{' '}
              <span className="confirm-modal-username">
                {deleteTarget.username}
                {getKoreanObjectParticle(deleteTarget.username)}
              </span>{' '}
              입력해 주세요.
            </p>

            <form onSubmit={handleConfirmDelete}>
              <label className="field" htmlFor="deleteConfirmUsername">
                <span className="field-label">삭제 대상 아이디</span>
                <input
                  id="deleteConfirmUsername"
                  className="confirm-modal-input"
                  type="text"
                  autoComplete="off"
                  value={deleteConfirmInput}
                  onChange={(event) => {
                    setDeleteConfirmInput(event.target.value);
                    setDeleteModalError('');
                  }}
                  placeholder={deleteTarget.username}
                  autoFocus
                />
              </label>

              {deleteModalError ? (
                <p className="confirm-modal-error">{deleteModalError}</p>
              ) : null}

              <div className="button-row modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={handleCloseDeleteModal}
                  disabled={Boolean(deletingId)}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="danger"
                  disabled={
                    Boolean(deletingId)
                    || deleteConfirmInput.trim() !== deleteTarget.username
                  }
                >
                  {deletingId ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default UserManagementPage;
